import type { MouseEvent } from "react";

import { Icons } from "../../shared/ui";
import { Badge } from "../../shared/ui/Badge";
import { cn } from "../../shared/ui/cn";
import { GroupLabel } from "../../shared/ui/GroupLabel";
import { adminHref, type AdminSectionId } from "./adminRoute";
import {
  groupLabel,
  SECTION_GLYPHS,
  SECTION_GROUPS,
  sectionBadge,
  sectionLabel,
} from "./adminSections";
import type { OperationsSnapshot } from "./operations";
import { useShellT } from "./useShellT";
import { useT } from "../../shared/i18n";

/**
 * Master list of the console sections, grouped into cards. `screen` is the
 * full-screen list of narrow screens (chevrons, large rows); `column` the
 * persistent left column of wide screens. Rows are real links so every section
 * has a URL.
 */
export function AdminNav({
  active,
  variant,
  snapshot,
  onSelect,
}: {
  active: AdminSectionId | null;
  variant: "screen" | "column";
  snapshot: OperationsSnapshot;
  onSelect: (section: AdminSectionId) => void;
}) {
  const t = useT();
  const shellT = useShellT();
  const column = variant === "column";
  return (
    <nav
      aria-label={t("admin.tabs.label")}
      className={cn(
        "flex min-w-0 flex-col gap-5",
        column ? "sticky top-2 w-56 shrink-0 self-start" : "w-full",
      )}
    >
      {SECTION_GROUPS.map((group) => (
        <div key={group.id} className="flex flex-col gap-2">
          <GroupLabel className="px-1">
            {groupLabel(shellT, group.id)}
          </GroupLabel>
          <div className="flex flex-col overflow-hidden rounded-card bg-raised shadow-card">
            {group.sections.map((id) => {
              const Glyph = SECTION_GLYPHS[id];
              const badge = sectionBadge(id, snapshot);
              const isActive = id === active;
              const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.button !== 0
                )
                  return;
                event.preventDefault();
                onSelect(id);
              };
              return (
                <a
                  key={id}
                  href={adminHref({ section: id })}
                  aria-current={isActive ? "page" : undefined}
                  onClick={onClick}
                  className={cn(
                    "flex min-h-(--row-min-h) min-w-0 items-center gap-3 border-l-[3px] px-3.5 text-body font-medium",
                    "transition-[background-color,color] motion-fast",
                    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
                    isActive
                      ? "border-accent bg-accent-subtle text-accent-fg"
                      : "border-transparent text-fg hover:bg-hover-overlay",
                  )}
                >
                  <Glyph size={20} />
                  <span className="min-w-0 flex-1 truncate">
                    {sectionLabel(shellT, id)}
                  </span>
                  {badge !== null && (
                    <Badge tone={badge.tone}>{badge.count}</Badge>
                  )}
                  {!column && (
                    <Icons.ChevronRight size={18} className="text-fg-subtle" />
                  )}
                </a>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
